import type { ClassificationDialogData, ClassificationContentType } from '@core/domain';
import { formatTimestamp } from '@core/utils';
import { Badge, Chat, Dialog, Eye, Icon, IconButton, IconButtonSize, IconSize, Photo, Profile, StatusCircleBlock, Volume } from '@soteria/design-system-ve';

import * as styles from './ClassificationDialog.css';
import { ClassificationDialogContent } from './ClassificationDialogContent';

export interface ClassificationDialogProps {
  /** Classification data to display */
  data: ClassificationDialogData;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Whether content should be blurred */
  isContentBlurred: boolean;
  /** Callback to toggle content blur */
  onToggleBlur: () => void;
}

/** Get the appropriate icon for the content type */
function getContentTypeIcon(contentType: ClassificationContentType) {
  switch (contentType) {
    case 'audio':
      return Volume;
    case 'image':
      return Photo;
    case 'username':
      return Profile;
    case 'text':
    case 'mixed':
    default:
      return Chat;
  }
}

/** Get dialog title based on content type */
function getDialogTitle(contentType: ClassificationContentType): string {
  switch (contentType) {
    case 'audio':
      return 'Audio Classification Result';
    case 'image':
      return 'Image Classification Result';
    case 'text':
      return 'Text Classification Result';
    case 'username':
      return 'Username Classification Result';
    case 'mixed':
      return 'Mixed Content Classification Result';
    default:
      return 'Classification Result';
  }
}

/**
 * Shared ClassificationDialog component for displaying classification results.
 *
 * This is a core component that can be used across domains (classification-testing,
 * content-search, moderation). Each domain provides an adapter to convert its
 * domain-specific types to ClassificationDialogData.
 */
export function ClassificationDialog({ data, isOpen, onClose, isContentBlurred, onToggleBlur }: ClassificationDialogProps) {
  const dialogTitle = getDialogTitle(data.contentType);
  const dialogDescription = formatTimestamp(data.timestamp);

  const customHeader = (
    <div>
      <div className={styles.titleContainer}>
        <Icon icon={getContentTypeIcon(data.contentType)} iconSize={IconSize.Small} />
        <h2 className={styles.dialogTitle}>{dialogTitle}</h2>
        {!data.isSafe && (
          <IconButton
            buttonSize={IconButtonSize.Small}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
      {dialogDescription && <p className={styles.dialogDescription}>{dialogDescription}</p>}
    </div>
  );

  return (
    <Dialog cancelAction={{ label: 'Close' }} customHeader={customHeader} isOpen={isOpen} size="xxxl" onClose={onClose}>
      <div className={styles.statusBadge}>
        <Badge className={data.isSafe ? styles.safeBadge : styles.unsafeBadge} text={data.isSafe ? 'SAFE' : 'UNSAFE'} />
      </div>

      <ClassificationDialogContent data={data} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur} />
    </Dialog>
  );
}
